import { useQuery } from '@apollo/client';
import { Box, Card, CardContent, styled, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { sortBy } from 'remeda';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { ModalContainer } from 'src/components/ModalContainer';
import { gql } from 'src/gql';
import { DEFAULT_CLIENTS_QUERY_LIMIT } from 'src/graphql';
import { useMembershipEmail } from 'src/graphql/entities/membership-email';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';

const SendMembershipEmailClientsQuery = gql(`
  query SendMembershipEmailClientsQuery($params: QueryParamsWithSearch) @entity(name: "Client") {
    getClient(queryParams: $params) {
      records {
        id
        clientId
        firstName
        lastName
        fullName @client
        primaryEmail {
          id
          value
        }
        emails {
          id
          value
        }
      }
    }
  }
`);

const ClientCardContainer = styled(Card, {
  name: 'ClientCardContainer',
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1),
  background: isSelected ? theme.palette.custom.OV.sys.surfaceVariant : 'none',
  border: '1px solid',
  borderColor: isSelected
    ? theme.palette.custom.OV.sys.secondary
    : theme.palette.custom.OV.sys.outlineVariant,
  cursor: 'pointer',
  '&:hover': {
    background: theme.palette.custom.OV.sys.surfaceVariant,
  },
}));

const ClientCard = ({
  name,
  email,
  isSelected,
  onClick,
  testId,
}: {
  name: string;
  email: string;
  isSelected: boolean;
  onClick: () => void;
  testId: string;
}) => {
  if (!name || !email) {
    return null;
  }

  return (
    <ClientCardContainer isSelected={isSelected} onClick={onClick} data-testid={testId}>
      <DynamicIcon mr={1} name="person" />
      <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle2" component="div" display="flex" alignItems="center">
            {name}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {email}
        </Typography>
      </CardContent>
    </ClientCardContainer>
  );
};

export const SendMembershipEmailModal = () => {
  const { closeModal } = useModalMethods(Modals.SendMembershipEmail);
  const { isOpened, modalProps } = useModalState(Modals.SendMembershipEmail);

  const [selectedClient, setSelectedClient] = useState('');

  const filters = useMemo(
    () =>
      modalProps?.address?.addressId
        ? [
            {
              attribute: 'addresses/addressId',
              operator: '=',
              value: modalProps?.address?.addressId || '',
            },
          ]
        : [],
    [modalProps?.address?.addressId],
  );

  const { data } = useQuery(SendMembershipEmailClientsQuery, {
    variables: {
      params: {
        filters,
        limit: DEFAULT_CLIENTS_QUERY_LIMIT,
      },
    },
    skip: !filters.length,
  });

  const primaryClientId = modalProps?.address?.primaryClientId;
  const clients = useMemo(
    () =>
      sortBy(
        (data?.getClient?.records ?? []).filter(
          (client): client is NonNullable<typeof client> => client !== null,
        ),
        (client) => client.clientId !== primaryClientId,
      ),
    [data, primaryClientId],
  );

  const { sendMembershipEmail, loading: isSubmittingMembershipEmail } = useMembershipEmail();

  const { showSuccessAlert } = useNotifications();

  const onSubmit = useCallback(async () => {
    await sendMembershipEmail({
      addressId: modalProps?.address?.addressId || '',
      clientId: selectedClient,
    });

    showSuccessAlert('Email sent!');
    setSelectedClient('');
    closeModal();
  }, [
    closeModal,
    modalProps?.address?.addressId,
    selectedClient,
    sendMembershipEmail,
    showSuccessAlert,
  ]);

  const onClose = useCallback(() => {
    setSelectedClient('');
    closeModal();
  }, [closeModal]);

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Send membership email"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'send-membership-email-cancel' },
        {
          label: 'Send email',
          kind: 'primary',
          onClick: onSubmit,
          disabled: !selectedClient,
          loading: isSubmittingMembershipEmail,
          loadingLabel: 'Sending',
          testId: 'send-membership-email-submit',
        },
      ]}
      onClose={onClose}
      data-testid="send-membership-email-modal"
    >
      <Typography variant="subtitle1" gutterBottom>
        Select a client:
      </Typography>
      {clients.map((client) => (
        <ClientCard
          key={client.clientId}
          name={client.fullName}
          email={client.primaryEmail?.value || client.emails?.[0]?.value || ''}
          isSelected={selectedClient === client.clientId}
          onClick={() => setSelectedClient(client.clientId)}
          testId={`send-membership-email-client-${client.clientId}`}
        />
      ))}
      {!clients.length && <Typography>No clients found</Typography>}
    </ModalContainer>
  );
};
